import React, { useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import Panel from '../ui/Panel'
import Btn from '../ui/Btn'
import { Note } from './peopleUI'
import './people.css'

/*
 * Add a player of the mobile app from the back office.
 *
 * The "Add User" button used to open the player view with nothing in it —
 * that screen only shows an existing player and lets you switch them on or
 * off, so there was no way to create anyone. This is the create form it
 * opens now.
 *
 * The player is saved as a complete, activated profile: they can sign in to
 * the app straight away with the email and password set here, and scouts can
 * find them immediately. The server checks every choice against the database
 * — the position must belong to the sport, the country must exist.
 */

const api = () => process.env.REACT_APP_API_SERVER
const auth = () => ({ headers: { Authorization: 'Bearer ' + sessionStorage.getItem('apiToken') } })
const rowsOf = (res) => (res && res.data && Array.isArray(res.data.results)
  ? res.data.results
  : Array.isArray(res && res.data) ? res.data : [])

const EMPTY = {
  FirstName: '', LastName: '', Nickname: '',
  Email: '', Password: '',
  DateOfBirth: '', MobilePhone: '',
  SportID: '', PositionID: '', CountryID: '',
  Height: '', Weight: '', LeftRight: '1', Team: ''
}

export default function NewPlayerForm({ defaultSportID, onCreated, onCancel }) {
  const [sports, setSports] = useState([])
  const [positions, setPositions] = useState([])
  const [countries, setCountries] = useState([])
  const [loadFailed, setLoadFailed] = useState(false)

  const [f, setF] = useState({ ...EMPTY, SportID: defaultSportID ? String(defaultSportID) : '' })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [created, setCreated] = useState(null)

  useEffect(() => {
    Promise.all([
      axios.get(api() + 'admin/sports', auth()),
      axios.get(api() + 'admin/users/positions', auth()),
      axios.get(api() + 'admin/countries', auth())
    ]).then(([s, p, c]) => {
      setSports(rowsOf(s))
      setPositions(rowsOf(p))
      setCountries(rowsOf(c).filter(row => row && row.CountryID).sort((a, b) => String(a.Country).localeCompare(String(b.Country))))
    }).catch(() => setLoadFailed(true))
  }, [])

  const set = (key) => (e) => setF(prev => ({ ...prev, [key]: e.target.value }))

  /* Positions follow the sport; a position from the other sport is dropped. */
  const sportPositions = useMemo(
    () => positions.filter(p => String(p.SportID) === String(f.SportID)),
    [positions, f.SportID]
  )
  useEffect(() => {
    if (f.PositionID && !sportPositions.some(p => String(p.PositionID) === String(f.PositionID))) {
      setF(prev => ({ ...prev, PositionID: '' }))
    }
  }, [sportPositions, f.PositionID])

  const submit = (e) => {
    e.preventDefault()
    setError('')
    setBusy(true)
    axios.post(api() + 'admin/users/create', f, auth()).then(res => {
      setBusy(false)
      if (res.data && res.data.err === 0) {
        setCreated({ name: res.data.name, email: res.data.email })
        setF({ ...EMPTY, SportID: f.SportID })
        if (onCreated) onCreated()
      } else {
        setError((res.data && res.data.msg) || 'The player could not be created.')
      }
    }).catch(() => {
      setBusy(false)
      setError('The player could not be created. Check your connection and try again.')
    })
  }

  return (
    <Panel title="Add Player" subtitle="A complete app profile, ready to sign in">
      <form className="pp-form" onSubmit={submit}>

        {loadFailed ? (
          <Note tone="crit">The sports, positions or countries could not be loaded. Close this form and try again.</Note>
        ) : null}

        {created ? (
          <Note tone="good" onDismiss={() => setCreated(null)}>
            <b>{created.name}</b> has been added. They can sign in to the app with <b>{created.email}</b> and
            the password you set.
          </Note>
        ) : null}

        {error ? <Note tone="crit" onDismiss={() => setError('')}>{error}</Note> : null}

        <fieldset className="pp-fieldset">
          <legend className="pp-legend">Player</legend>
          <div className="pp-grid">
            <div className="pp-field">
              <label className="pp-label" data-req="1" htmlFor="np-first">First name</label>
              <input id="np-first" className="pp-input" required maxLength={80} value={f.FirstName} onChange={set('FirstName')} />
            </div>
            <div className="pp-field">
              <label className="pp-label" data-req="1" htmlFor="np-last">Last name</label>
              <input id="np-last" className="pp-input" required maxLength={80} value={f.LastName} onChange={set('LastName')} />
            </div>
            <div className="pp-field">
              <label className="pp-label" htmlFor="np-nick">Nickname</label>
              <input id="np-nick" className="pp-input" maxLength={60} value={f.Nickname} onChange={set('Nickname')} />
            </div>
            <div className="pp-field">
              <label className="pp-label" data-req="1" htmlFor="np-dob">Date of birth</label>
              <input id="np-dob" className="pp-input" type="date" required value={f.DateOfBirth} onChange={set('DateOfBirth')} />
            </div>
            <div className="pp-field">
              <label className="pp-label" htmlFor="np-phone">Mobile phone</label>
              <input id="np-phone" className="pp-input" type="tel" maxLength={30} value={f.MobilePhone} onChange={set('MobilePhone')} />
            </div>
            <div className="pp-field">
              <label className="pp-label" data-req="1" htmlFor="np-country">Country</label>
              <select id="np-country" className="pp-select" required value={f.CountryID} onChange={set('CountryID')}>
                <option value="">Choose a country</option>
                {countries.map(c => <option key={c.CountryID} value={c.CountryID}>{c.Country}</option>)}
              </select>
            </div>
          </div>
        </fieldset>

        <fieldset className="pp-fieldset">
          <legend className="pp-legend">Sport profile</legend>
          <div className="pp-grid">
            <div className="pp-field">
              <label className="pp-label" data-req="1" htmlFor="np-sport">Sport</label>
              <select id="np-sport" className="pp-select" required value={f.SportID} onChange={set('SportID')}>
                <option value="">Choose a sport</option>
                {sports.map(s => <option key={s.SportID} value={s.SportID}>{s.SportName}</option>)}
              </select>
            </div>
            <div className="pp-field">
              <label className="pp-label" data-req="1" htmlFor="np-pos">Position</label>
              <select id="np-pos" className="pp-select" required value={f.PositionID} onChange={set('PositionID')} disabled={!f.SportID}>
                <option value="">{f.SportID ? 'Choose a position' : 'Choose the sport first'}</option>
                {sportPositions.map(p => <option key={p.PositionID} value={p.PositionID}>{p.Title}</option>)}
              </select>
            </div>
            <div className="pp-field">
              <label className="pp-label" htmlFor="np-height">Height (cm)</label>
              <input id="np-height" className="pp-input" type="number" min={100} max={250} value={f.Height} onChange={set('Height')} />
            </div>
            <div className="pp-field">
              <label className="pp-label" htmlFor="np-weight">Weight (kg)</label>
              <input id="np-weight" className="pp-input" type="number" min={25} max={200} value={f.Weight} onChange={set('Weight')} />
            </div>
            <div className="pp-field">
              <label className="pp-label" htmlFor="np-side">Preferred side</label>
              <select id="np-side" className="pp-select" value={f.LeftRight} onChange={set('LeftRight')}>
                <option value="1">Right</option>
                <option value="2">Left</option>
                <option value="3">Both</option>
              </select>
            </div>
            <div className="pp-field">
              <label className="pp-label" htmlFor="np-team">Team</label>
              <input id="np-team" className="pp-input" maxLength={120} value={f.Team} onChange={set('Team')} />
            </div>
          </div>
        </fieldset>

        <fieldset className="pp-fieldset">
          <legend className="pp-legend">Sign-in</legend>
          <div className="pp-grid">
            <div className="pp-field">
              <label className="pp-label" data-req="1" htmlFor="np-email">Email</label>
              <input id="np-email" className="pp-input" type="email" required maxLength={200} autoComplete="off" value={f.Email} onChange={set('Email')} />
              <span className="pp-hint">The player signs in to the app with this.</span>
            </div>
            <div className="pp-field">
              <label className="pp-label" data-req="1" htmlFor="np-pass">Password</label>
              <input id="np-pass" className="pp-input" type="text" required minLength={8} maxLength={64} autoComplete="off" value={f.Password} onChange={set('Password')} />
              <span className="pp-hint">8 characters or more. The player can change it later in the app.</span>
            </div>
          </div>
        </fieldset>

        <div className="pp-actions">
          <Btn onClick={onCancel}>Back to the list</Btn>
          <Btn variant="accent" type="submit" disabled={busy || loadFailed}>
            {busy ? 'Adding…' : 'Add player'}
          </Btn>
        </div>
      </form>
    </Panel>
  )
}
